import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

// Configure axios with the same baseURL as api.js
axios.defaults.baseURL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:5000'
  : '/api';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeContext, setUpgradeContext] = useState(null);

  const fetchUsageStats = async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get('/usage/stats');
      setSubscriptionData(response.data);
    } catch (error) {
      console.error('Failed to fetch usage stats:', error);
      // Set default free tier if fetch fails
      setSubscriptionData({
        plan: 'free',
        limits: {
          personas: { current: 0, limit: 3, unlimited: false, percentage: 0 },
          groups: { current: 0, limit: 3, unlimited: false, percentage: 0 },
          saved_items: { current: 0, limit: 20, unlimited: false, percentage: 0 },
          wishlists: { current: 0, limit: 1, unlimited: false, percentage: 0 }
        }
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsageStats();
    
    // Refresh on window focus (helps catch changes made in other tabs/admin panel)
    const handleFocus = () => {
      fetchUsageStats();
    };
    
    // Listen for custom refresh events (e.g., from admin panel)
    const handleRefresh = () => {
      fetchUsageStats();
    };
    
    window.addEventListener('focus', handleFocus);
    window.addEventListener('subscription-updated', handleRefresh);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('subscription-updated', handleRefresh);
    };
  }, [isAuthenticated]);

  const refreshUsageStats = () => {
    return fetchUsageStats();
  };

  const isProUser = subscriptionData?.plan === 'pro';
  const isFreeUser = subscriptionData?.plan === 'free';

  const getUsage = (feature) => {
    return subscriptionData?.limits?.[feature] || {
      current: 0,
      limit: 0,
      unlimited: false,
      percentage: 0
    };
  };

  const isNearLimit = (feature, threshold = 80) => {
    const usage = getUsage(feature);
    return usage.percentage >= threshold;
  };

  const isAtLimit = (feature) => {
    const usage = getUsage(feature);
    return !usage.unlimited && usage.current >= usage.limit;
  };

  const promptUpgrade = (context) => {
    setUpgradeContext(context);
    setShowUpgradeModal(true);
  };

  const closeUpgradeModal = () => {
    setShowUpgradeModal(false);
    setUpgradeContext(null);
  };

  const handleLimitError = (error) => {
    if (error.response?.status === 403 && error.response?.data?.upgrade_required) {
      const errorData = error.response.data;
      promptUpgrade({
        feature: errorData.feature,
        limit: errorData.limit,
        message: errorData.message,
        currentPlan: errorData.current_plan
      });
      return true;
    }
    return false;
  };

  const value = {
    subscriptionData,
    loading,
    isProUser,
    isFreeUser,
    getUsage,
    isNearLimit,
    isAtLimit,
    refreshUsageStats,
    showUpgradeModal,
    upgradeContext,
    promptUpgrade,
    closeUpgradeModal,
    handleLimitError
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
